/**
 * A visitor's own layout-direction choice (CAIV-728).
 *
 * Clubs can hand the decision to the visitor: with `layout.allow_direction_change` on, the chat
 * shows a control that flips between left-to-right and right-to-left. The club's setting is then
 * the starting point rather than the final word, so this holds only what the visitor picked —
 * `null` until they touch it, which keeps "not chosen" distinguishable from "chose the same value
 * the club did" and lets a later change of the club setting still move an untouched widget.
 *
 * Persisted per widget in localStorage, next to the session, so the choice survives a reload the
 * way it would in any other app that offers it.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Direction } from '../i18n/useDirection';

const STORAGE_KEY_PREFIX = 'clubplanner_widget_direction_';

function storageKey(widgetId: string): string {
  return `${STORAGE_KEY_PREFIX}${widgetId}`;
}

function readStored(widgetId: string): Direction | null {
  if (typeof window === 'undefined' || !widgetId) return null;
  try {
    const raw = localStorage.getItem(storageKey(widgetId));
    return raw === 'ltr' || raw === 'rtl' ? raw : null;
  } catch {
    // Private browsing and blocked storage: the choice just won't outlive the tab.
    return null;
  }
}

export interface UseVisitorDirectionReturn {
  /** What the visitor picked, or null if they haven't. */
  visitorDirection: Direction | null;
  /** Record a choice (or clear it with null). */
  setVisitorDirection: (direction: Direction | null) => void;
}

export function useVisitorDirection(widgetId: string): UseVisitorDirectionReturn {
  const [visitorDirection, setStateDirection] = useState<Direction | null>(() =>
    readStored(widgetId)
  );

  // A different widget on the same page (or a re-init with another channel) has its own choice.
  useEffect(() => {
    setStateDirection(readStored(widgetId));
  }, [widgetId]);

  const setVisitorDirection = useCallback(
    (direction: Direction | null) => {
      setStateDirection(direction);
      if (typeof window === 'undefined' || !widgetId) return;
      try {
        if (direction) {
          localStorage.setItem(storageKey(widgetId), direction);
        } else {
          localStorage.removeItem(storageKey(widgetId));
        }
      } catch {
        // Non-fatal: the toggle still works for this session.
      }
    },
    [widgetId]
  );

  return { visitorDirection, setVisitorDirection };
}

export default useVisitorDirection;
